import { keepPreviousData, QueryKey } from '@tanstack/react-query'
import { SubgraphType } from 'config/subgraph'
import {
  GetPositionByIdQuery,
  GetPositionsByOwnerQuery,
  SearchPositionsByOwnerByIdQuery,
  SearchPositionsByOwnerQuery,
  useGetPositionByIdQuery,
  useGetPositionsByOwnerQuery,
  useSearchPositionsByOwnerByIdQuery,
  useSearchPositionsByOwnerQuery,
} from 'queries/v3/generated'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHasRecentPositionTransactions } from 'state/transactions/useRecentPositionTransactions'
import { PositionDetails } from 'types/position'
import { type PositionTokenAmounts, getPositionTokenAmounts, getPositionUsdValue } from 'utils/getPositionUsdValue'

import { v3QueryKeys } from './subgraph/v3'
import { useConnection } from './useConnection'
import useDebounce from './useDebounce'
import { useSubgraphConfig } from './useSubgraphConfig'
import { useGetTokenPrices } from './useTokenPrice'

interface UseV3PositionsResults {
  loading: boolean
  positions: PositionDetails[]
  openPositions: PositionDetails[]
  closedPositions: PositionDetails[]
  totalCount: number
  currentPage: number
  totalPages: number
  goToPage: (page: number) => void
  refetch: () => void
  isSearching: boolean
  isError: boolean
  error?: { name?: string; message?: string } | any
}

interface UseV3PositionResults {
  loading: boolean
  position: PositionDetails | undefined
}

interface PaginationParams {
  pageSize?: number
  enablePagination?: boolean
}

type SubgraphPosition =
  | GetPositionsByOwnerQuery['positions'][number]
  | SearchPositionsByOwnerQuery['t0s'][number]
  | SearchPositionsByOwnerByIdQuery['byId'][number]
  | NonNullable<GetPositionByIdQuery['position']>

const transformPosition = (position: SubgraphPosition | null | undefined): PositionDetails | null => {
  if (!position) return null

  const tickLower = parseInt(position.tickLower.tickIdx)
  const tickUpper = parseInt(position.tickUpper.tickIdx)
  const currentTick = parseInt(position.pool.tick || '0')
  const inRange = currentTick >= tickLower && currentTick < tickUpper

  return {
    tokenId: position.id,
    owner: position.owner,
    fee: parseInt(position.pool.feeTier),
    feeGrowthInside0LastX128: position.feeGrowthInside0LastX128,
    feeGrowthInside1LastX128: position.feeGrowthInside1LastX128,
    liquidity: position?.liquidity || '0',
    nonce: '0',
    operator: '0x',
    tickLower,
    tickUpper,
    token0: position?.token0.id || '',
    token1: position?.token1.id || '',
    tokensOwed0: '0',
    tokensOwed1: '0',
    inRange,
    token0Symbol: position?.token0.symbol || '',
    token1Symbol: position?.token1.symbol || '',
    token0Decimals: parseInt(position.token0.decimals) || 18,
    token1Decimals: parseInt(position.token1.decimals) || 18,
    poolTick: currentTick,
    token0Price: parseFloat(position.pool.token0Price),
    token1Price: parseFloat(position.pool.token1Price),
    depositedToken0: position?.depositedToken0,
    depositedToken1: position?.depositedToken1,
    withdrawnToken0: position?.withdrawnToken0,
    withdrawnToken1: position?.withdrawnToken1,
    collectedFeesToken0: position?.collectedFeesToken0,
    collectedFeesToken1: position?.collectedFeesToken1,
    pool: {
      id: position.pool.id,
      feeTier: position.pool.feeTier,
      tick: position.pool.tick,
      sqrtPrice: position.pool.sqrtPrice,
      liquidity: position.pool.liquidity,
      token0Price: position.pool.token0Price,
      token1Price: position.pool.token1Price,
      // Add the new fee growth fields
      feeGrowthGlobal0X128: position.pool.feeGrowthGlobal0X128,
      feeGrowthGlobal1X128: position.pool.feeGrowthGlobal1X128,
    },
    // Add tick fee growth data
    tickLowerData: {
      feeGrowthOutside0X128: position.tickLower.feeGrowthOutside0X128,
      feeGrowthOutside1X128: position.tickLower.feeGrowthOutside1X128,
    },
    tickUpperData: {
      feeGrowthOutside0X128: position.tickUpper.feeGrowthOutside0X128,
      feeGrowthOutside1X128: position.tickUpper.feeGrowthOutside1X128,
    },
  }
}

const normalizePositions = (positions: Array<SubgraphPosition | null | undefined>) => {
  const merged: PositionDetails[] = []
  for (const raw of positions) {
    const position = transformPosition(raw)
    if (position) {
      merged.push(position)
    }
  }

  const seen = new Set<string>()
  const unique: PositionDetails[] = []
  for (const position of merged) {
    const key = position.tokenId
    if (!key || seen.has(key)) continue
    seen.add(key)
    unique.push(position)
  }

  return unique
}

export const POSITIONS_PER_PAGE = 50

export function useV3PositionFromTokenId(tokenId: string | undefined): UseV3PositionResults {
  return useV3PositionByIdFromSubgraph(tokenId)
}

export function useV3Positions(
  account: string | null | undefined,
  userHideClosedPositions = false,
  paginationParams: PaginationParams = {},
  searchTerm?: string
): UseV3PositionsResults {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const hasRecentTransactions = useHasRecentPositionTransactions()
  const pollingInterval = hasRecentTransactions ? 5000 : 300000

  const { pageSize = POSITIONS_PER_PAGE, enablePagination = true } = paginationParams

  const [currentPage, setCurrentPage] = useState(0)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const trimmedSearch = debouncedSearchTerm?.trim() ?? ''
  const hasSearch = trimmedSearch.length > 0
  const isIdLike = /^\d+$/.test(trimmedSearch)
  const minLiquidity = userHideClosedPositions ? '0' : '-1'

  const owner = account?.toLowerCase() ?? ''
  const hasOwner = Boolean(owner)
  const first = 1000
  const skip = 0

  const isSearching = useMemo(() => {
    const hasActiveSearch = searchTerm && searchTerm.trim()
    const hasRawSearchButNotDebounced = hasActiveSearch && searchTerm !== debouncedSearchTerm
    return Boolean(hasRawSearchButNotDebounced)
  }, [searchTerm, debouncedSearchTerm])

  const baseQueryOptions = useMemo(
    () => ({
      refetchInterval: pollingInterval,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }),
    [pollingInterval]
  )

  const positionsQuery = useGetPositionsByOwnerQuery(
    { endpoint },
    { owner, first, skip, minLiquidity },
    {
      ...baseQueryOptions,
      enabled: Boolean(endpoint) && hasOwner && !hasSearch,
      queryKey: v3QueryKeys.positions(chainId, owner, minLiquidity, first, skip) as QueryKey,
      placeholderData: keepPreviousData,
    }
  )

  const searchByIdQuery = useSearchPositionsByOwnerByIdQuery(
    { endpoint },
    { owner, first, q: trimmedSearch, searchId: trimmedSearch, minLiquidity },
    {
      ...baseQueryOptions,
      enabled: Boolean(endpoint) && hasOwner && hasSearch && isIdLike,
      queryKey: v3QueryKeys.searchPositionsById(
        chainId,
        owner,
        trimmedSearch,
        minLiquidity,
        first,
        trimmedSearch
      ) as QueryKey,
      placeholderData: keepPreviousData,
    }
  )

  const searchQuery = useSearchPositionsByOwnerQuery(
    { endpoint },
    { owner, q: trimmedSearch, minLiquidity },
    {
      ...baseQueryOptions,
      enabled: Boolean(endpoint) && hasOwner && hasSearch && !isIdLike,
      queryKey: v3QueryKeys.searchPositions(chainId, owner, minLiquidity, trimmedSearch) as QueryKey,
      placeholderData: keepPreviousData,
    }
  )

  useEffect(() => {
    setCurrentPage(0)
  }, [userHideClosedPositions, account, debouncedSearchTerm])

  const positions = useMemo(() => {
    if (!hasSearch) {
      const ownerData = positionsQuery.data
      if (!ownerData) return [] as PositionDetails[]
      const normalized = normalizePositions(ownerData.positions ?? [])
      return normalized
    }

    if (isIdLike) {
      const searchData = searchByIdQuery.data
      if (!searchData) return [] as PositionDetails[]
      const buckets = [
        ...(searchData.byId ?? []),
        ...(searchData.t0s ?? []),
        ...(searchData.t0n ?? []),
        ...(searchData.t1s ?? []),
        ...(searchData.t1n ?? []),
      ]
      const normalized = normalizePositions(buckets)
      return normalized
    }

    const searchData = searchQuery.data
    if (!searchData) return [] as PositionDetails[]
    const buckets = [
      ...(searchData.t0s ?? []),
      ...(searchData.t0n ?? []),
      ...(searchData.t1s ?? []),
      ...(searchData.t1n ?? []),
    ]
    const normalized = normalizePositions(buckets)
    return normalized
  }, [hasSearch, isIdLike, positionsQuery.data, searchByIdQuery.data, searchQuery.data])

  const tokenAddresses = useMemo(() => {
    const set = new Set<string>()
    for (const p of positions) {
      if (p.token0) set.add(p.token0.toLowerCase())
      if (p.token1) set.add(p.token1.toLowerCase())
    }
    return Array.from(set)
  }, [positions])

  const { prices: tokenPrices, isLoading: isTokenPricesLoading } = useGetTokenPrices(tokenAddresses)

  const debouncedTokenPrices = useDebounce(tokenPrices, 300)

  const positionAmounts = useMemo(() => {
    if (!positions.length) return new Map<string, PositionTokenAmounts | null>()

    const amountsMap = new Map<string, PositionTokenAmounts | null>()
    positions.forEach((position) => {
      amountsMap.set(position.tokenId, getPositionTokenAmounts(position, chainId))
    })

    return amountsMap
  }, [positions, chainId])

  const priceMap = useMemo(() => {
    const effectiveTokenPrices = debouncedTokenPrices ?? tokenPrices
    if (!effectiveTokenPrices) return null

    const map: Record<string, number> = {}
    tokenAddresses.forEach((addr, i) => {
      const price = effectiveTokenPrices[i]
      if (typeof price === 'number') map[addr] = price
    })

    return map
  }, [debouncedTokenPrices, tokenAddresses, tokenPrices])

  const sortedPositions = useMemo(() => {
    if (!positions.length || !priceMap) return positions

    const valueMap = new Map<string, number>()
    positions.forEach((position) => {
      const amounts = positionAmounts.get(position.tokenId)
      valueMap.set(position.tokenId, getPositionUsdValue(position, chainId, priceMap, amounts ?? null))
    })

    return [...positions].sort((a, b) => (valueMap.get(b.tokenId) || 0) - (valueMap.get(a.tokenId) || 0))
  }, [positions, priceMap, positionAmounts, chainId])

  const paginatedPositions = useMemo(() => {
    if (!enablePagination) return sortedPositions
    const start = currentPage * pageSize
    const end = start + pageSize
    return sortedPositions.slice(start, end)
  }, [sortedPositions, enablePagination, currentPage, pageSize])

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page)
    window.scrollTo(0, 0)
  }, [])

  const memoizedRefetch = useCallback(() => {
    setCurrentPage(0)
    if (!hasSearch) {
      positionsQuery.refetch?.()
    } else if (isIdLike) {
      searchByIdQuery.refetch?.()
    } else {
      searchQuery.refetch?.()
    }
  }, [hasSearch, isIdLike, positionsQuery.refetch, searchByIdQuery.refetch, searchQuery.refetch])

  const [openPositions, closedPositions] = useMemo(() => {
    if (!paginatedPositions) return [[], []]

    return paginatedPositions.reduce<[PositionDetails[], PositionDetails[]]>(
      (acc, position) => {
        const isZero = !position.liquidity || position.liquidity === '0' || position.liquidity === '0x0'
        acc[isZero ? 1 : 0].push(position)
        return acc
      },
      [[], []]
    )
  }, [paginatedPositions])

  const totalPositionsCount = sortedPositions.length

  const totalPages = useMemo(() => {
    if (!enablePagination || !totalPositionsCount) return 1
    return Math.max(Math.ceil(totalPositionsCount / pageSize), 1)
  }, [enablePagination, totalPositionsCount, pageSize])

  const activeError = !hasSearch ? positionsQuery.error : isIdLike ? searchByIdQuery.error : searchQuery.error
  const activeLoading =
    (!hasSearch ? positionsQuery.isLoading : isIdLike ? searchByIdQuery.isLoading : searchQuery.isLoading) ||
    (positions.length > 0 && isTokenPricesLoading)

  useEffect(() => {
    if (activeError) {
      console.error('Error fetching positions from subgraph:', activeError)
    }
  }, [activeError])

  return {
    loading: activeLoading,
    positions: paginatedPositions ?? [],
    openPositions,
    closedPositions,
    totalCount: totalPositionsCount,
    currentPage,
    totalPages,
    goToPage,
    refetch: memoizedRefetch,
    isSearching,
    isError: Boolean(activeError),
    error: activeError as any,
  }
}

export function useV3PositionByIdFromSubgraph(positionId: string | undefined): UseV3PositionResults {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const hasRecentTransactions = useHasRecentPositionTransactions()
  const pollingInterval = hasRecentTransactions ? 5000 : 400000

  const normalizedId = positionId?.toLowerCase() ?? ''

  const { data, isLoading, error } = useGetPositionByIdQuery(
    { endpoint },
    { positionId: normalizedId },
    {
      enabled: Boolean(endpoint) && Boolean(normalizedId),
      refetchInterval: pollingInterval,
      refetchOnReconnect: true,
      staleTime: 5 * 60 * 1000,
      queryKey: v3QueryKeys.positionById(chainId, normalizedId) as QueryKey,
      select: (response: GetPositionByIdQuery) => transformPosition(response?.position) ?? undefined,
    }
  )

  useEffect(() => {
    if (error) {
      console.error('Error fetching position from subgraph:', error)
    }
  }, [error])

  return { loading: isLoading, position: data }
}
